/* SmartnetDetailsViewRenderer.js */

"use strict";
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const _ = require("lodash");
const FileName = require("@gluenetworks/steve_utilities").fileName;
class SmartnetDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("smartnet-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._formsComponent = new FormsComponent();
        this._renderingForm = false;
        this._viewActionsAdded = false;
        this._activeText = document.createElement("span");
        this._fileNamer = FileName;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._removeViewActions();
        let promArr = [this._formsComponent.shutdown];
        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._viewActionsAdded = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._logger = undefined;
                this._activeText = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "SmartnetDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        return this._model.whenModelIsReady().then(
            () => {
                return this._renderCoverageDetails();
            },
            (err) => err
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderCoverageDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValue();
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);

                this._renderingForm = true;
                return this._formsComponent.renderer
                    .render()
                    .then(() => this._renderActiveStatus())
                    .then(() => {
                        if (this._model._deviceCoverage) {
                            this._addViewActions();
                        }
                    })
                    .then(() => {
                        this._renderingForm = false;
                    });
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "SmartnetDetailsViewRenderer._renderCoverageDetails() error."
                );
            }
        );
    }

    _addViewActions() {
        if (!this._viewActionsAdded) {
            this.model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "DownloadSmartNet",
                handler: (this._onDownloadSmartNet = this._onDownloadSmartNet.bind(this)),
            });
            this._viewActionsAdded = true;
        }
    }

    _removeViewActions() {
        if (this._viewActionsAdded) {
            this.model.gameEditor.clearViewActions();
        }
    }

    _renderActiveStatus() {
        let pristineDeviceProperties = this._model._deviceCoverage
            ? this._model._deviceCoverage.getPristineProperties()
            : null;

        let activeInput = document.querySelector("[placeholder='Active']"),
            activeContainer = activeInput.parentElement;
        // TODO To be refactored with feed value
        if (pristineDeviceProperties && pristineDeviceProperties.data.is_covered) {
            this._activeText.innerHTML = pristineDeviceProperties.data.is_covered;
            this._activeText.setAttribute("class", "active_status bold");
        } else {
            this._activeText.innerHTML = "Not Available";
            this._activeText.setAttribute("class", "active_status");
        }
        activeContainer.appendChild(this._activeText);

        // Remove standard input
        activeInput.remove();
        activeInput = undefined;
    }

    _onDownloadSmartNet() {
        this.model.gameEditor.disableViewAction("DownloadSmartNet");
        const deviceDetials = this.model._device,
            coverageData = this._model._deviceCoverage
                ? this._model._deviceCoverage.getPristineProperties()
                : null,
            //fileName = "SmartNet-" + deviceDetials.name + "-" + new Date(Date.now()).toISOString();
            fileName = this._fileNamer({
                solution: "DM",
                general: "SmartNetDetails",
                specific: deviceDetials.name,
                suffix: "csv",
            });
        let printdata = {};
        printdata.Name = deviceDetials.name;
        printdata.Description = deviceDetials.description;
        if (coverageData) {
            printdata.Active = coverageData.data.is_covered;
            printdata.ContractNumber = coverageData.data.service_contract_number;
            printdata.ContractEndDate = coverageData.data.covered_product_line_end_date;
            printdata.ContractSiteCustomerName = coverageData.data.contract_site_customer_name;
            printdata.ContractSiteAddress = coverageData.data.contract_site_address1;
            printdata.ContractSiteCity = coverageData.data.contract_site_city;
            printdata.ContractSiteStateProvince = coverageData.data.contract_site_state_province;
            printdata.ContractSiteCountry = coverageData.data.contract_site_country;
            printdata.ServiceDescription = coverageData.data.service_line_descr;
            printdata.ServiceNumber = coverageData.data.sr_no;
            printdata.WarrantyType = coverageData.data.warranty_type;
            printdata.WarrantyExpiration = coverageData.data.warranty_end_date;
            printdata.WarrantyDescription = coverageData.data.warranty_type_description;
        }
        this.model.gameEditor.saveCSVFile(fileName, this.model.gameEditor.convertToCSV(printdata));
        this.model.gameEditor.enableViewAction("DownloadSmartNet");
        return this;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged() {
        if (!this._renderingForm) {
            this._formsComponent.setPristineValue(this._getPristineValue());
            // TODO Workaround as _formsComponent doesn't provide promises or events to wait for rendering
            _.delay(_.bind(this._renderActiveStatus, this), 500);
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getPristineValue() {
        let pristineProperties = this._model._device.getPristineProperties();
        let pristineCoverageProperties = this._model._deviceCoverage
            ? this._model._deviceCoverage.getPristineProperties()
            : null;
        let pristineValue = {};
        if (pristineProperties) {
            pristineValue.name = pristineProperties.name;
            pristineValue.description = pristineProperties.description;
            if (pristineCoverageProperties && pristineCoverageProperties.data) {
                pristineValue.active = pristineCoverageProperties.data.is_covered;
                pristineValue.contractNumber =
                    pristineCoverageProperties.data.service_contract_number;
                // pristineValue.contractEndDate = pristineValue.contractEndDate = _.get(pristineProperties, ["customFieldValues", "0c6644ce-a700-476a-82f9-16e4821ed430"]);
                pristineValue.contractEndDate =
                    pristineCoverageProperties.data.covered_product_line_end_date;
                pristineValue.contactInformation = {};
                pristineValue.contactInformation.contractSiteCustomerName =
                    pristineCoverageProperties.data.contract_site_customer_name;
                pristineValue.contactInformation.contractSiteAddress =
                    pristineCoverageProperties.data.contract_site_address1;
                pristineValue.contactInformation.contractSiteCity =
                    pristineCoverageProperties.data.contract_site_city;
                pristineValue.contactInformation.contractSiteStateProvince =
                    pristineCoverageProperties.data.contract_site_state_province;
                pristineValue.contactInformation.contractSiteCountry =
                    pristineCoverageProperties.data.contract_site_country;
                pristineValue.serviceDescription =
                    pristineCoverageProperties.data.service_line_descr;
                pristineValue.serviceNumber = pristineCoverageProperties.data.sr_no;
                pristineValue.warrantyType = pristineCoverageProperties.data.warranty_type;
                pristineValue.warrantyExpiration =
                    pristineCoverageProperties.data.warranty_end_date;
                pristineValue.warrantyDescription =
                    pristineCoverageProperties.data.warranty_type_description;
            }
        }
        return pristineValue;
    }

    _getJtype() {
        let jtype = {};

        jtype.name = "";
        jtype["name@Display.label"] = "Name";
        jtype["name@Display.readOnly"] = true;

        jtype.description = "";
        jtype["description@Display.label"] = "Description";
        jtype["description@Display.description"] = "Device Description";
        jtype["description@Display.placeholder"] = "Not Available";
        jtype["description@Display.readOnly"] = true;

        jtype.active = "";
        jtype["active@Display.label"] = "Active";
        jtype["active@Display.description"] = "SmartNet Contract Status";
        jtype["active@Display.readOnly"] = true;

        jtype.contractNumber = "";
        jtype["contractNumber@Display.label"] = "Contract Number";
        jtype["contractNumber@Display.description"] = "SmartNet Contract Number";
        jtype["contractNumber@Display.placeholder"] = "Not Available";
        jtype["contractNumber@Display.readOnly"] = true;

        jtype.contractEndDate = "";
        jtype["contractEndDate@Display.label"] = "Contract End";
        jtype["contractEndDate@Display.description"] = "SmartNet Contract End";
        jtype["contractEndDate@Display.placeholder"] = "Not Available";
        jtype["contractEndDate@Display.readOnly"] = true;

        jtype.lineBreak1 = null;
        jtype["lineBreak1@Display.label"] = "";

        jtype.contactInformation = {};
        jtype["contactInformation@Display.label"] = "Contact Information";
        jtype["contactInformation@Display.description"] = "SmartNet Primary Contact Information";

        jtype.contactInformation.contractSiteCustomerName = "";
        jtype.contactInformation["contractSiteCustomerName@Display.label"] = "Customer Name";
        jtype.contactInformation["contractSiteCustomerName@Display.placeholder"] = "Not Available";
        jtype.contactInformation["contractSiteCustomerName@Display.readOnly"] = true;
        jtype.contactInformation.contractSiteAddress = "";
        jtype.contactInformation["contractSiteAddress@Display.label"] = "Address";
        jtype.contactInformation["contractSiteAddress@Display.placeholder"] = "Not Available";
        jtype.contactInformation["contractSiteAddress@Display.readOnly"] = true;
        jtype.contactInformation.contractSiteCity = "";
        jtype.contactInformation["contractSiteCity@Display.label"] = "City";
        jtype.contactInformation["contractSiteCity@Display.placeholder"] = "Not Available";
        jtype.contactInformation["contractSiteCity@Display.readOnly"] = true;
        jtype.contactInformation.contractSiteStateProvince = "";
        jtype.contactInformation["contractSiteStateProvince@Display.label"] = "State/Province";
        jtype.contactInformation["contractSiteStateProvince@Display.placeholder"] = "Not Available";
        jtype.contactInformation["contractSiteStateProvince@Display.readOnly"] = true;
        jtype.contactInformation.contractSiteCountry = "";
        jtype.contactInformation["contractSiteCountry@Display.label"] = "Country";
        jtype.contactInformation["contractSiteCountry@Display.placeholder"] = "Not Available";
        jtype.contactInformation["contractSiteCountry@Display.readOnly"] = true;

        jtype.serviceDescription = "";
        jtype["serviceDescription@Display.label"] = "Service Description";
        jtype["serviceDescription@Display.placeholder"] = "Not Available";
        jtype["serviceDescription@Display.readOnly"] = true;

        jtype.serviceNumber = "";
        jtype["serviceNumber@Display.label"] = "Service Number";
        jtype["serviceNumber@Display.placeholder"] = "Not Available";
        jtype["serviceNumber@Display.readOnly"] = true;

        jtype.warrantyType = "";
        jtype["warrantyType@Display.label"] = "Warranty Type";
        jtype["warrantyType@Display.placeholder"] = "Not Available";
        jtype["warrantyType@Display.readOnly"] = true;

        jtype.warrantyExpiration = "";
        jtype["warrantyExpiration@Display.label"] = "Warranty Expiration";
        jtype["warrantyExpiration@Display.description"] = "Warranty Expiration Date";
        jtype["warrantyExpiration@Display.placeholder"] = "Not Available";
        jtype["warrantyExpiration@Display.readOnly"] = true;

        jtype.warrantyDescription = "";
        jtype["warrantyDescription@Display.label"] = "Warranty Description";
        jtype["warrantyDescription@Display.placeholder"] = "Not Available";
        jtype["warrantyDescription@Display.readOnly"] = true;

        // TODO Warranty Information property not availabel in the Entity

        // jtype.warrantyInformation = "";
        // jtype["warrantyInformation@Display.label"] = "Warranty Information";
        // jtype["warrantyInformation@Display.description"] = " Further Warranty Details";
        // jtype["warrantyInformation@Display.readOnly"] = true;

        return jtype;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = SmartnetDetailsViewRenderer;
